import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import CustomeButton from "~/components/Button";
import { FileUpload } from "primereact/fileupload";
import Attachments from "./Attachments";
import { getName } from "country-list";
import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import fileUploadLogo from "../../../assets/upload-file.svg";
import noPreviewLogo from "../../../assets/no-preview.svg";
import { Button } from "primereact/button";
import SearchBar from "~/components/searchBar";
import PropTypes from "prop-types";
import Categories from "./utils/categories.json";
import { InputNumber } from "primereact/inputnumber";
import useFileUpload from "~/utils/useFileUpload";
import { removeToken, getAdHocEscalationDisplayName } from "~/utils/fileUtils";

const FileUploadModal = ({
  showModal,
  hideUploadModal,
  fileSupported,
  uploadedFile,
  setUploadedFile,
  getService,
  entitydata,
  employeeData,
  requestdata,
  backButton,
  placeholder,
  processdata = {},
  cancelButtonName,
  loading = false,
  close = true,
  isEmployeeAttachment = false,
  relatedType = "",
  shareholderdata = {},
  officerdata = {},
  directordata = {},
  categories = Categories,
}) => {
  const [category, setCategory] = useState(null);
  const [subCategory, setSubCategory] = useState(null);
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState("");
  const [showSubcategories, setShowSubCategories] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [filteredCategories, setFilteredCategories] = useState(categories);
  const [year, setYear] = useState(null);
  const fileUploadRef = useRef(null);
  const categoryRef = useRef(null);
  const subcategoryRef = useRef(null);

  const {
    uuidToUlid,
    SERVICE_CODES,
    getFileVersion,
    getFormattedProcessName,
    REQUEST_REASON_CODES,
    isFetchingVersion,
  } = useFileUpload();

  const getFileSizeMB = (file) => (file.size / (1024 * 1024)).toFixed(2);

  const handleCategoryChange = (e) => {
    setCategory(e);
    setYear(null);
    setShowSubCategories(true);
  };

  const onFileUpload = (e) => {
    const uniqueFiles = e.files
      .filter((eventFile) => !files.includes(eventFile))
      .map((eventFile) => {
        if (
          !fileSupported.includes(eventFile.name.split(".").pop().toLowerCase())
        ) {
          return { file: eventFile, error: { state: true, visible: true } };
        } else {
          if (getFileSizeMB(eventFile) > 20) {
            return { file: eventFile, error: { state: true, visible: true } };
          } else {
            return { file: eventFile, error: { state: false, visible: false } };
          }
        }
      });

    setFiles([...files, ...uniqueFiles]);
    e.files = [];
    fileUploadRef.current.clear();
  };

  const onFileRemove = (fileToRemove) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
  };

  const fileDropHandler = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      fileUploadRef.current.files = files;
      const newFiles = Array.from(files);
      setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    } else {
      console.error("No files were dropped");
    }
  };

  const getFileType = (fileName) => {
    return fileName.split(".").pop();
  };

  const handleReplace = (event, fileToReplace) => {
    const newFile = event.files[0];
    const newFiles = files.map((f) => {
      if (f.file === fileToReplace) {
        if (
          !fileSupported.includes(newFile.name.split(".").pop().toLowerCase())
        ) {
          return { file: newFile, error: { state: true, visible: true } };
        } else if (getFileSizeMB(newFile) > 20) {
          return { file: newFile, error: { state: true, visible: true } };
        } else {
          return { file: newFile, error: { state: false, visible: false } };
        }
      }
      return f;
    });
    setFiles(newFiles);
  };

  const getFileName = async (file) => {
    const fileType = getFileType(file?.name);
    const yearPart = year ? `_${year}` : "";
    let fileName = "";
    if (relatedType === "PROCESS") {
      const isPublic = location.pathname.includes("public");
      const tags = {
        category: file?.category,
        subCategory: file?.subcategory,
        entity: uuidToUlid(processdata?.entity),
        service: SERVICE_CODES[getService()?.name],
        is_public: isPublic === true ? "true" : "false",
        country: processdata?.entityData.country,
        process: uuidToUlid(processdata?.id),
        year: new Date().getFullYear().toString(),
        temporary_file: "false",
      };
      let version = `${await getFileVersion(tags)}`;
      files[0]["file"]["versionNumber"] = Number(version);
      if (uploadedFile?.length > 0) {
        const { category, subcategory } = file;
        const matchingFiles = uploadedFile.filter(
          (item) =>
            item.file.category === category &&
            item.file.subcategory === subcategory,
        );
        if (matchingFiles.length > 0) {
          version =
            Math.max(
              ...matchingFiles.map((item) => Number(item.file.versionNumber)),
            ) + 1;
        }
        files[0]["file"]["versionNumber"] = version;
      }
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${getService()?.name}_${getFormattedProcessName(processdata)}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (relatedType === "ENTITIES_PLUS") {
      const tags = {
        entity: uuidToUlid(entitydata?.id),
        country: entitydata?.country,
        core_entity_info: "Core Entity Information",
        temporary_file: "false",
        category: file?.category,
        subCategory: file?.subcategory,
        year: new Date().getFullYear().toString(),
      };
      const version = `${await getFileVersion(tags)}`;
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${getService()?.name}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (relatedType === "SHAREHOLDER") {
      const tags = {
        entity: uuidToUlid(entitydata?.id),
        country: entitydata?.country,
        temporary_file: "false",
        shareHolder: uuidToUlid(shareholderdata?.id),
        category: file?.category,
        subCategory: file?.subcategory,
        year: new Date().getFullYear().toString(),
      };
      const version = `${await getFileVersion(tags)}`;
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${getService()?.name}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (relatedType === "DIRECTOR") {
      const tags = {
        entity: uuidToUlid(entitydata?.id),
        country: entitydata?.country,
        temporary_file: "false",
        director: uuidToUlid(directordata?.id),
        category: file?.category,
        subCategory: file?.subcategory,
        year: new Date().getFullYear().toString(),
      };
      const version = `${await getFileVersion(tags)}`;
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${getService()?.name}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (relatedType === "OFFICER") {
      const tags = {
        entity: uuidToUlid(entitydata?.id),
        country: entitydata?.country,
        temporary_file: "false",
        officer: uuidToUlid(officerdata?.id),
        category: file?.category,
        subCategory: file?.subcategory,
        year: new Date().getFullYear().toString(),
      };
      const version = `${await getFileVersion(tags)}`;
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${getService()?.name}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (relatedType === "REQUEST") {
      const subCategoryCode =
        typeof file?.subcategory === "object"
          ? file.subcategory?.code
          : file?.subcategory;
      const tags = {
        entity: uuidToUlid(entitydata.id),
        service: SERVICE_CODES[requestdata?.serviceData?.name],
        country: entitydata.country,
        request: uuidToUlid(requestdata?.id),
        request_reason: REQUEST_REASON_CODES[requestdata?.reason],
        year: new Date().getFullYear().toString(),
        category: file?.category,
        subCategory: subCategoryCode,
        temporary_file: "false",
      };
      let version = `${await getFileVersion(tags)}`;
      files[0]["file"]["versionNumber"] = Number(version);
      if (uploadedFile?.length > 0) {
        const matchingFiles = uploadedFile.filter(
          (item) =>
            item.file.category === file?.category &&
            (typeof item.file.subcategory === "object"
              ? item.file.subcategory?.code
              : item.file.subcategory) === subCategoryCode,
        );
        if (matchingFiles.length > 0) {
          version =
            Math.max(
              ...matchingFiles.map((item) => Number(item.file.versionNumber)),
            ) + 1;
        }
        files[0]["file"]["versionNumber"] = version;
      }
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${requestdata?.reason}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (
      relatedType === "SHAREHOLDER_ONBOARDING" ||
      relatedType === "OFFICER_ONBOARDING"
    ) {
      let version = 1;
      files[0]["file"]["versionNumber"] = version;
      if (uploadedFile?.length > 0) {
        const { category, subcategory } = file;
        const matchingFiles = uploadedFile.filter(
          (item) =>
            item.file.category === category &&
            item.file.subcategory === subcategory,
        );
        if (matchingFiles.length > 0) {
          version =
            Math.max(
              ...matchingFiles.map((item) => Number(item.file.versionNumber)),
            ) + 1;
        }
        files[0]["file"]["versionNumber"] = version;
      }
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${getService()?.name}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (relatedType === "AD_HOC" || relatedType === "ESCALATION") {
      const subCategoryCode =
        typeof file?.subcategory === "object"
          ? file.subcategory?.code
          : file?.subcategory;
      let version = 1;
      files[0]["file"]["versionNumber"] = version;
      if (uploadedFile?.length > 0) {
        const matchingFiles = uploadedFile.filter(
          (item) =>
            item.file.category === file?.category &&
            (typeof item.file.subcategory === "object"
              ? item.file.subcategory?.code
              : item.file.subcategory) === subCategoryCode,
        );
        if (matchingFiles.length > 0) {
          version =
            Math.max(
              ...matchingFiles.map((item) => Number(item.file.versionNumber)),
            ) + 1;
        }
        files[0]["file"]["versionNumber"] = version;
      }
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${getService()?.reason}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    } else if (isEmployeeAttachment && employeeData) {
      const tags = {
        entity: uuidToUlid(employeeData?.entity_id),
        country: entitydata?.country,
        temporary_file: "false",
        employee: uuidToUlid(employeeData?.id),
        category: file?.category,
        subCategory: file?.subcategory,
        year: new Date().getFullYear().toString(),
        ...(file?.customYear && { customYear: file?.customYear.toString() }),
      };
      removeToken();
      const container =
        file?.subcategory === "TAX_STATEMENTS" ? "ess" : "documents";
      const version = `${await getFileVersion(tags, container)}`;
      fileName = `${entitydata?.name}_${getName(entitydata?.country)}_${employeeData.employee_identifier}_${employeeData.last_name}${yearPart}_${category?.name}_${subCategory?.name}_${version}.${fileType}`;
    }

    return fileName;
  };

  const uploadFile = async () => {
    files[0]["file"]["category"] = category.code;
    const isAdHocOrEscalation =
      relatedType === "REQUEST" ||
      relatedType === "AD_HOC" ||
      relatedType === "ESCALATION";
    files[0]["file"]["subcategory"] = isAdHocOrEscalation
      ? { code: subCategory.code, name: subCategory.name }
      : subCategory.code;
    if (description) {
      files[0]["file"]["description"] = description;
    }
    if (year) {
      files[0]["file"]["customYear"] = year;
    }
    files[0]["file"]["fileName"] = await getFileName(files[0]["file"]);
    await setUploadedFile(files);
    if (close) {
      hideUploadModal();
    }
  };
  useEffect(() => {
    clear();
  }, [showModal]);
  const descriptionHandler = (event) => {
    setDescription(event.target.value);
  };

  const onUpdateVisibility = (fileToRemove) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file === fileToRemove
          ? { ...file, error: { state: true, visible: false } }
          : file,
      ),
    );
  };

  const clear = () => {
    setCategory(null);
    setFiles([]);
    setSubCategory(null);
    setShowCategories(false);
    setShowSubCategories(false);
    setYear(null);
    setDescription("");
    fileUploadRef?.current?.clear();
    setSelectedIndex(null);
    setFilteredCategories(categories);
  };
  const handleCloseModal = () => {
    hideUploadModal();
    clear();
  };

  const searchEntity = (value) => {
    if (!value) setFilteredCategories(categories);
    const filtered = categories.filter((cat) => {
      return cat?.name.toLowerCase().includes(value.toLowerCase());
    });
    setFilteredCategories(filtered);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOutsideCategory =
        categoryRef.current && !categoryRef.current.contains(event.target);
      const isClickOutsideSubcategory =
        subcategoryRef.current &&
        !subcategoryRef.current.contains(event.target);

      if (showCategories && !showSubcategories && isClickOutsideCategory) {
        setShowCategories(false);
      }

      if (
        showCategories &&
        showSubcategories &&
        isClickOutsideCategory &&
        isClickOutsideSubcategory
      ) {
        setShowCategories(false);
        setShowSubCategories(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCategories, showSubcategories]);

  const footer = () => {
    return (
      <div className="footer-btn-container">
        <CustomeButton
          label={cancelButtonName ? cancelButtonName : "Cancel"}
          variant="outlined"
          className="cancel-button"
          onClick={() => {
            handleCloseModal();
          }}
        />
        <CustomeButton
          label="Upload File"
          variant="contained"
          className="upload-file-btn"
          disabled={
            loading ||
            !category ||
            !subCategory ||
            (isEmployeeAttachment &&
              category?.code === "PAYSLIPS_AND_PAYROLL_REPORTS" &&
              subCategory?.code === "TAX_STATEMENTS" &&
              !year) ||
            files.length < 1 ||
            files[0]?.error?.state ||
            files[0]?.error?.visible ||
            isFetchingVersion
          }
          onClick={uploadFile}
          loading={loading || isFetchingVersion}
          iconPos="right"
        />
      </div>
    );
  };

  return (
    <Dialog
      header={
        <div className="flex justify-content-between align-items-center">
          <div className="flex align-items-center">
            {backButton && (
              <i
                className="pi pi-arrow-left back-icon ml-3"
                onClick={handleCloseModal}
                style={{ cursor: "pointer" }}
              ></i>
            )}
            <span className="model-head">Upload File</span>
          </div>
          <i
            className="pi pi-times cross-icon"
            onClick={handleCloseModal}
            style={{ cursor: "pointer" }}
          ></i>
        </div>
      }
      visible={showModal}
      className="file-upload-dialog"
      onHide={() => {
        handleCloseModal();
      }}
      footer={footer}
    >
      <div className="file-upload-container" onDrop={() => fileDropHandler()}>
        <div className="file-upload-section">
          <div
            className={`file-drag-drop-section ${files.length > 0 ? "no-preview" : ""}`}
          >
            <img
              src={files.length === 0 ? fileUploadLogo : noPreviewLogo}
              alt="file-upload"
            />
            {files.length === 0 ? (
              <p className="drag-drop-text">
                {placeholder
                  ? placeholder
                  : "Click anywhere to select and upload a file."}
              </p>
            ) : (
              <p className="no-preview-text">No Preview Available</p>
            )}
            {files.length === 0 && (
              <FileUpload
                ref={fileUploadRef}
                name="files"
                customUpload={true}
                onSelect={onFileUpload}
                chooseOptions={{
                  icon: "pi pi-upload",
                  label: "Select File",
                  iconOnly: false,
                  className: "select-file-btn",
                }}
                mode="advanced"
              />
            )}
          </div>
          <div className="file-format-section">
            <p className="format-text">
              Supported formats: PDF, DOC, CSV, XLS, JPEG, JPG, PNG, TXT, XML
            </p>
            <p className="file-size-text">Maximum file size: 20MB</p>
          </div>

          {files.length > 0 && (
            <div className="attachment-layout">
              <div className="attachment-container">
                {files.map((file, index) => {
                  const isAdHocOrEscalation =
                    relatedType === "REQUEST" ||
                    relatedType === "AD_HOC" ||
                    relatedType === "ESCALATION";
                  const displayName =
                    isAdHocOrEscalation && category && subCategory
                      ? getAdHocEscalationDisplayName(
                          subCategory.name,
                          new Date(),
                          file.file?.name,
                        )
                      : undefined;
                  return (
                    <Attachments
                      file={file.file}
                      key={index}
                      fileHandler={() => {
                        onFileRemove(file);
                      }}
                      handleReplace={(e) => handleReplace(e, file.file)}
                      showReplaceButton={true}
                      icon="pi-times"
                      error={file.error}
                      getFileSizeMB={getFileSizeMB}
                      handleErrorVisibility={() => {
                        onUpdateVisibility(file);
                      }}
                      displayName={displayName}
                      useUploadActions={true}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="details-section">
          <p className="details-title"> File Details</p>
          <div className="details-field-container">
            <p className="category-label">Category*</p>
            <Button
              outlined
              icon="pi pi-angle-down"
              className="category-dropdown-btn"
              placeholder="Select Category"
              onClick={() => setShowCategories(true)}
            >
              <span className="category-selected">
                {subCategory?.name || "Select Category"}
              </span>
            </Button>

            {showCategories && (
              <div className="category-container" ref={categoryRef}>
                <div className="search-container">
                  <SearchBar onChange={(e) => searchEntity(e.target.value)} />
                </div>

                <ul>
                  {filteredCategories.length > 0 ? (
                    filteredCategories.map((cat, index) => (
                      <li
                        key={index}
                        onClick={() => {
                          handleCategoryChange(cat);
                          setSelectedIndex(index);
                        }}
                        className={selectedIndex === index && "selected"}
                      >
                        {cat?.name}
                        <i className="pi pi-angle-right" />
                      </li>
                    ))
                  ) : (
                    <p className="empty-search">No results found</p>
                  )}
                </ul>
              </div>
            )}
            {showSubcategories && category && (
              <div className="sub-options-container" ref={subcategoryRef}>
                <ul>
                  {category?.subCategory.map((subOption, index) => (
                    <li key={index}>
                      <RadioButton
                        name={subOption?.name}
                        value={subOption?.name}
                        onChange={(e) => {
                          setSubCategory({
                            name: e.value,
                            code: subOption?.code,
                          });
                          setShowSubCategories(false);
                          setShowCategories(false);
                          setFilteredCategories(categories);
                          setSelectedIndex(null);
                        }}
                        checked={subCategory?.name === subOption?.name}
                        inputId={subOption?.name}
                      />
                      <label className="ml-2" htmlFor={subOption?.name}>
                        {subOption?.name}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {isEmployeeAttachment &&
              category?.code === "PAYSLIPS_AND_PAYROLL_REPORTS" &&
              subCategory?.code === "TAX_STATEMENTS" && (
                <div className="details-field-container">
                  <p className="description-label">Year*</p>
                  <InputNumber
                    value={year}
                    placeholder="Enter Year"
                    onChange={(e) => {
                      const rawValue = e.value;
                      const numericValue = parseInt(rawValue, 10);
                      setYear(isNaN(numericValue) ? null : numericValue);
                    }}
                    useGrouping={false}
                    minFractionDigits={0}
                    maxFractionDigits={0}
                    min={0}
                    mode="decimal"
                  />
                </div>
              )}
          </div>
          <div className="details-field-container">
            <p className="description-label">Description (Optional)</p>
            <InputTextarea
              value={description}
              onChange={descriptionHandler}
              placeholder="Describe the file you are uploading"
              autoResize={true}
              maxLength={200}
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};

FileUploadModal.propTypes = {
  showModal: PropTypes.bool,
  hideUploadModal: PropTypes.func,
  fileSupported: PropTypes.array,
  setUploadedFile: PropTypes.func,
  categories: PropTypes.array,
};

export default FileUploadModal;
